import type { DownlineAccountRow } from '@/api/map/plStatement'
import { toPlStatementRowFromDownline } from '@/api/map/plStatement'
import type { PlStatementRowView } from '@/api/map/plStatement'
import type { Role } from '@/features/hierarchy/hierarchy'

export type MemberStatementStatus =
  | 'Rollback'
  | 'Invalid'
  | 'P&L Rollback'
  | 'Invalidate P&L'
  | '-'

/** Lab tabs: Player P&L Statement vs P&L Statement (`showTotalPl`). */
export type MemberStatementMode = 'player' | 'total'

export type MemberStatementRow = PlStatementRowView & {
  isInvalid?: boolean
  isRollback?: boolean
  isPNLInvalid?: boolean
  isPNLRollBack?: boolean
  /** Lab gameName (SportsBook / Live Casino / …). */
  gameName: string
  /** Lab subGame (Cricket / Tennis / …). */
  subGame: string
  previousBalance?: number
  nextBalance?: number
  exposure?: number
}

/** Account Statement Status — Invalid Bet + Invalidate P&L (Strix list UX). */
export function memberStatementStatus(row: {
  isInvalid?: boolean
  isRollback?: boolean
  isPNLInvalid?: boolean
  isPNLRollBack?: boolean
}): MemberStatementStatus {
  if (row.isRollback) return 'Rollback'
  if (row.isInvalid) return 'Invalid'
  if (row.isPNLRollBack) return 'P&L Rollback'
  if (row.isPNLInvalid) return 'Invalidate P&L'
  return '-'
}

/** Event cell prefixes for Invalid Bet + Invalidate P&L rows. */
export function memberStatementEventLabel(row: {
  isInvalid?: boolean
  isRollback?: boolean
  isPNLInvalid?: boolean
  isPNLRollBack?: boolean
  eventName?: string
  marketName?: string
}): string {
  const prefix = row.isRollback
    ? 'Rollback bets-'
    : row.isInvalid
      ? 'Invalid bets-'
      : row.isPNLRollBack
        ? 'P&L Rollback-'
        : row.isPNLInvalid
          ? 'Invalidate P&L-'
          : ''
  const event = String(row.eventName ?? '').trim()
  const match = String(row.marketName ?? '').trim()
  const body = match ? `${event} - ${match}` : event
  return `${prefix}${body}`.trim()
}

/** Lab Description transfer suffixes (appended to gameName). */
const STATEMENT_TRANSFER_SUBGAMES = new Set([
  'downlineTransfer',
  'uplineTransfer',
  'Re-Deposit',
  'Deposit',
  'Withdrawal',
])

export function isStatementTransferSubGame(subGame: string | null | undefined): boolean {
  return STATEMENT_TRANSFER_SUBGAMES.has(String(subGame ?? '').trim())
}

/** Settlement often stamps sub_game as the literal string "None". */
export function statementSubGameLabel(raw: string | null | undefined): string {
  const s = String(raw ?? '').trim()
  if (!s || /^none$/i.test(s) || /^null$/i.test(s) || s === '-') return ''
  return s
}

/**
 * Lab Description column = `gameName`.
 * Sheet #46: SportsBook also shows sport (subGame) beside it — `SportsBook - Cricket`.
 * Transfers keep Lab `SportsBook - uplineTransfer` style.
 * Missing/"None" subGame → product only (not `SportsBook - None`).
 */
export function memberStatementKindLabel(row: {
  gameName?: string
  subGame?: string
  sport?: string
}): string {
  const game = String(row.gameName ?? '').trim() || 'Betting P&L'
  // Use API subGame only — `sport` may be invented Cricket when API stamped "None".
  const sub = statementSubGameLabel(row.subGame)
  if (!sub) return game
  if (game === 'SportsBook' || STATEMENT_TRANSFER_SUBGAMES.has(sub)) {
    return `${game} - ${sub}`
  }
  return game
}

/** Lab Event cell — Racing/Casino product label, else event+market with status prefix. */
export function memberStatementDescription(row: {
  eventName?: string
  marketName?: string
  gameName?: string
  sport?: string
  subGame?: string
  isInvalid?: boolean
  isRollback?: boolean
  isPNLInvalid?: boolean
  isPNLRollBack?: boolean
}): string {
  const game = String(row.gameName ?? '').trim()
  const sub = statementSubGameLabel(row.sport) || statementSubGameLabel(row.subGame)
  if (game === 'Racing' || game === 'Live Casino' || game === 'WAC' || game === 'Premium-Sportsbook') {
    return sub ? `${game} - ${sub}` : game
  }
  return memberStatementEventLabel(row)
}

/**
 * Lab money column: Player P&L = player netAmount; P&L Statement = viewed
 * seat's net (Player → same as player net).
 */
export function memberStatementAmount(
  row: Pick<MemberStatementRow, 'memberPnl' | 'split'>,
  mode: MemberStatementMode,
  viewedRole: Role,
): number {
  if (mode === 'player' || viewedRole === 'player') return row.memberPnl
  const seat = row.split?.[viewedRole]
  return typeof seat === 'number' && Number.isFinite(seat) ? seat : row.memberPnl
}

/** Lab Total Net Share — sum of the active money column. */
export function memberStatementTotalNetShare(
  rows: Array<Pick<MemberStatementRow, 'memberPnl' | 'split'>>,
  mode: MemberStatementMode,
  viewedRole: Role,
): number {
  return rows.reduce((sum, row) => sum + memberStatementAmount(row, mode, viewedRole), 0)
}

/** Map CM member Account Statement row → list row + Lab game/subGame. */
export function toMemberStatementRow(row: DownlineAccountRow): MemberStatementRow {
  const base = toPlStatementRowFromDownline(row)
  return {
    ...base,
    isInvalid: Boolean(row.isInvalid),
    isRollback: Boolean(row.isRollback),
    isPNLInvalid: Boolean(row.isPNLInvalid),
    isPNLRollBack: Boolean(row.isPNLRollBack),
    gameName: String(row.gameData.gameName ?? base.game ?? 'SportsBook'),
    // Real sport/transfer only — literal API "None" → empty (not SportsBook - None).
    subGame: statementSubGameLabel(row.gameData.subGame),
    ...(row.previousBalance != null ? { previousBalance: row.previousBalance } : {}),
    ...(row.nextBalance != null || row.currBalance != null
      ? { nextBalance: row.nextBalance ?? row.currBalance }
      : {}),
    ...(row.exposure != null ? { exposure: row.exposure } : {}),
  }
}

/** datetime-local value from a Date (local wall clock) — Lab / Agent Account Statement. */
export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** From = daysBack at 00:00; to = today end-of-day when `endOfDay`. */
export function isoDatetimeOffset(daysBack: number, endOfDay = false): string {
  const d = new Date()
  d.setDate(d.getDate() - daysBack)
  if (endOfDay) d.setHours(23, 59, 0, 0)
  else d.setHours(0, 0, 0, 0)
  return toDatetimeLocalValue(d)
}

/** Card subtitle range: `2026-10-01 00:00 → 2026-10-28 23:59`. */
export function statementRangeLabel(from: string, to: string): string {
  const fmt = (v: string) => v.slice(0, 16).replace('T', ' ')
  return `${fmt(from)} → ${fmt(to)}`
}
